import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@/locales/i18n-provider";
import { NavigationList } from "@/surfaces/standalone/components/navigation-list";
import { systemConfigurationGroups } from "@/surfaces/standalone/navigation/navigation-model";
import { useVisibleNavigationGroups } from "@/surfaces/standalone/navigation/use-navigation";
import { cn } from "@/utils";

type SystemConfigurationSidebarProps = {
  collapsed?: boolean;
  onNavigate?: () => void;
  onToggle: () => void;
  toggleLabel: string;
};

export function SystemConfigurationSidebar({ collapsed = false, onNavigate, onToggle, toggleLabel }: SystemConfigurationSidebarProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const groups = useVisibleNavigationGroups(systemConfigurationGroups);

  return (
    <TooltipProvider key={collapsed ? "collapsed" : "expanded"} delayDuration={100}>
      <div className="flex h-full flex-col bg-background text-sidebar-foreground">
        <div className={cn("pb-0 pt-3", collapsed ? "px-2" : "px-3")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={collapsed ? t("shell.returnWorkspace") : undefined}
                className={cn(
                  "h-10 w-full gap-1 rounded-xl text-muted-foreground hover:text-primary/80 cursor-pointer text-xs",
                  collapsed ? "justify-center px-0" : "justify-start px-3",
                )}
                variant="ghost"
                onClick={() => {
                  navigate("/");
                  onNavigate?.();
                }}
              >
                <Icon className="size-4" icon="tabler:arrow-left" />
                {collapsed ? null : t("shell.returnWorkspace")}
              </Button>
            </TooltipTrigger>
            {collapsed ? (
              <TooltipContent side="right" sideOffset={8}>
                {t("shell.returnWorkspace")}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </div>

        <nav className={cn("flex-1 overflow-x-hidden overflow-y-auto", collapsed ? "p-2" : "p-3")} aria-label={t("shell.systemNavigation")}>
          <div className="flex flex-col gap-5">
            {groups.map((group) => (
              <section key={group.labelKey}>
                {collapsed ? null : <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">{t(group.labelKey)}</p>}
                <NavigationList collapsed={collapsed} items={group.items} onNavigate={onNavigate} />
              </section>
            ))}
          </div>
        </nav>

        <div className={cn("shrink-0", collapsed ? "p-2" : "p-3")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={toggleLabel}
                className={cn("h-10 w-full gap-3 rounded-xl text-muted-foreground", collapsed ? "justify-center px-0" : "justify-start px-3")}
                variant="ghost"
                onClick={onToggle}
              >
                <Icon className="size-4" icon={collapsed ? "tabler:layout-sidebar-left-expand" : "tabler:layout-sidebar-left-collapse"} />
                {collapsed ? null : <span className="text-xs">{toggleLabel}</span>}
              </Button>
            </TooltipTrigger>
            {collapsed ? (
              <TooltipContent side="right" sideOffset={8}>
                {toggleLabel}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
